<script lang="ts">
  import CTA from "@kontextwerk/web-sdk/ui/actions/CTA.svelte"
  import WorkflowSummary from "@kontextwerk/web-sdk/ui/layout/WorkflowSummary.svelte"
  import type { WorkflowSummaryModel } from "@kontextwerk/web-sdk/core/layout/workflowSummary"
  import StepActionCTA from "../../../../../../../../../../_shared/_modules/step-action-cta"
  import type {
    BatchUploadReferenceData,
    BatchUploadStepContext,
    BatchUploadWorkflowEvent,
    ContactOrdersOutput,
    CreateWorkOrdersOutput,
    PlanAppointmentsOutput,
  } from "../../_shared/_logic/batch-upload-contract"
  import {
    buildExpectedConsumptionSummary,
    buildBatchSummaryNextSteps,
    countExpectedMobileMessageSteps,
  } from "./_logic/summaryModel"

  type Props = {
    ctx: BatchUploadStepContext
    referenceData: BatchUploadReferenceData
    loadList: CreateWorkOrdersOutput
    planAppointments: PlanAppointmentsOutput
    contactOrders?: ContactOrdersOutput
    onEvent: (event: BatchUploadWorkflowEvent) => void | Promise<void>
  }

  const {
    ctx,
    referenceData,
    loadList,
    planAppointments,
    contactOrders = undefined,
    onEvent,
  }: Props = $props()
  let finishing = $state(false)

  const numberFormatter = new Intl.NumberFormat("de-DE")
  const effectiveEvents = $derived(
    contactOrders?.scheduleEventsAfterContactPlanning ?? planAppointments.createdEvents,
  )
  const createdOrdersCount = $derived(loadList.createdRows.map((row) => row.workOrder).length)
  const createdEventsCount = $derived(effectiveEvents.length)
  const createdContactOrdersCount = $derived(contactOrders?.createdContactOrders.length ?? 0)
  const selectedAddressCount = $derived(
    new Set(loadList.selectedRows.map((row) => row.propertyId)).size,
  )
  const mobileMessageCount = $derived(
    countExpectedMobileMessageSteps(contactOrders?.createdContactOrders ?? []),
  )
  const consumptionSummary = $derived(
    buildExpectedConsumptionSummary({
      createdOrdersCount,
      mobileMessageCount,
      billingPlanState: referenceData.billingPlanState,
      numberFormatter,
    }),
  )
  const nextSteps = $derived(buildBatchSummaryNextSteps())
  const summaryModel = $derived.by<WorkflowSummaryModel>(() => ({
    eyebrow: "Ergebnis",
    title: "Sammelplanung abgeschlossen",
    description:
      "Die ausgewählten Liegenschaften wurden in Aufträge, Termine und Kommunikationspläne überführt.",
    metrics: [
      {
        id: "created",
        label: "Aufträge geplant",
        value: createdOrdersCount,
        subLabel: createdOrdersCount === 1 ? "Auftrag erstellt" : "Aufträge erstellt",
        tone: createdEventsCount ? "success" : "neutral",
        facts: [
          { label: "Termine", value: numberFormatter.format(createdEventsCount) },
          { label: "Liegenschaften", value: numberFormatter.format(selectedAddressCount) },
        ],
      },
      {
        id: "communication",
        label: "Kundeninformation",
        value: "Vorbereitet",
        subLabel: "digitale Kontaktstrecken",
        tone: createdContactOrdersCount ? "success" : "neutral",
        tooltipContent: "Angelegte Ankündigungspläne bereiten die spätere Terminkommunikation vor.",
        facts: [
          { label: "Ankündigungspläne", value: numberFormatter.format(createdContactOrdersCount) },
          { label: "Mobile Nachrichten", value: numberFormatter.format(mobileMessageCount) },
        ],
      },
      {
        id: "consumption",
        label: "Erwarteter Verbrauch",
        value: consumptionSummary.valueLabel,
        subLabel: consumptionSummary.subLabel,
        tone: consumptionSummary.calculable ? "info" : "warning",
        tooltipContent: consumptionSummary.tooltipContent,
        facts: consumptionSummary.facts,
      },
    ],
    nextSteps: nextSteps.map((step, index) => ({
      id: `next-step-${index}`,
      label: step,
      tone: step.includes("Terminbrief") ? "info" : "neutral",
    })),
  }))

  const finishBatchPlanning = async () => {
    if (finishing) return

    finishing = true
    try {
      await onEvent({
        type: "finish",
        createdOrders: loadList.createdRows.map((row) => row.workOrder),
      })
    } catch {
      finishing = false
    }
  }
</script>

<div class="wrapper">
  <p class="hint">
    Die Sammelplanung ist abgeschlossen. Hier sehen Sie die wichtigsten Ergebnisse und was als
    Nächstes sinnvoll ist.
  </p>

  <WorkflowSummary model={summaryModel} surface="dark" />

  {#if !ctx.readOnly}
    <div class="step-actions">
      <CTA type="secondaryButton" content="Zurück" onClick={ctx.requestPrevious} />
      <StepActionCTA
        type="primaryButton"
        content={finishing ? "Aktualisiere..." : "Fertig"}
        disabled={finishing}
        disabledReason={finishing ? "Der Import-Abschluss wird gerade aktualisiert." : null}
        onClick={finishBatchPlanning}
      />
    </div>
  {/if}
</div>

<style lang="less">
  .wrapper {
    display: flex;
    flex-direction: column;
    gap: 1.2rem;
  }

  .hint {
    margin: 0;
    color: color-mix(in srgb, var(--text-100) 70%, transparent);
    font-size: 0.9rem;
    line-height: 1.35;
  }

  .step-actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.6rem;
    flex-wrap: wrap;
    margin-top: 0.4rem;
  }
</style>
